import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";

import { ActionDetailHeader } from "@/components/actions/ActionDetailHeader";
import { WorkspaceActionBar, WorkspaceItemType } from "@/components/actions/WorkspaceActionBar";
import { WorkspaceTimeline } from "@/components/actions/WorkspaceTimeline";
import { ActionIntroCard } from "@/components/actions/ActionIntroCard";
import { SEO } from "@/components/seo/SEO";

export default function ActionDetail() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeAddType, setActiveAddType] = useState<WorkspaceItemType | null>(null);

  // Fetch document details
  const { data: document, isLoading, error } = useQuery({
    queryKey: ['document-action', documentId],
    queryFn: async () => {
      if (!documentId) throw new Error('ID manquant');
      
      const { data, error } = await supabase
        .from('documents')
        .select('id, title, doc_type, expires_at, file_path, reminder_date, reminder_note')
        .eq('id', documentId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!documentId
  });

  // Mark as complete mutation
  const markComplete = useMutation({
    mutationFn: async () => {
      if (!documentId) throw new Error('ID manquant');
      
      // Update document_actions status if exists
      await supabase
        .from('document_actions')
        .update({ status: 'done' })
        .eq('document_id', documentId)
        .neq('status', 'done');

      // Mark the document expiry as dismissed from actions list
      await supabase
        .from('documents')
        .update({ expiry_dismissed_at: new Date().toISOString() })
        .eq('id', documentId);
    },
    onSuccess: () => {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      toast.success("Démarche terminée ! 🎉");
      queryClient.invalidateQueries({ queryKey: ['actions'] });
      navigate('/actions');
    },
    onError: () => toast.error("Erreur lors de la mise à jour")
  });

  const handleAddItem = (type: WorkspaceItemType) => {
    // Toggle if same type clicked
    setActiveAddType(prev => prev === type ? null : type);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Document introuvable</p>
        <Button variant="outline" onClick={() => navigate('/actions')}>
          Retour aux actions
        </Button>
      </div>
    );
  }

  // Map to the shape expected by ActionDetailHeader
  const headerDoc = {
    id: document.id,
    title: document.title,
    doc_type: document.doc_type,
    expires_at: document.expires_at,
    file_path: document.file_path
  };

  return (
    <>
      <SEO 
        title={`Planifier - ${document.title}`}
        description="Préparez votre démarche administrative"
      />
      
      <div className="min-h-screen bg-background pb-28">
        <div className="max-w-2xl mx-auto p-4 space-y-4">
          {/* Header with document summary */}
          <ActionDetailHeader document={headerDoc} />
          
          {/* Contextual intro with official link */}
          <ActionIntroCard docType={document.doc_type} />
          
          {/* Action bar - always visible */}
          <WorkspaceActionBar 
            onAdd={handleAddItem}
            activeType={activeAddType}
          />
          
          {/* Unified timeline */}
          <WorkspaceTimeline
            documentId={document.id}
            docType={document.doc_type}
            activeAddType={activeAddType}
            onCancelAdd={() => setActiveAddType(null)}
            initialReminderDate={document.reminder_date}
            initialReminderNote={document.reminder_note}
          />
        
          {/* Complete button - sticky inside main content to respect sidebar inset */}
          <div className="sticky bottom-4 mt-8">
            <Button
              size="lg"
              className="w-full gap-2 bg-success hover:bg-success/90 text-success-foreground shadow-lg"
              onClick={() => markComplete.mutate()}
              disabled={markComplete.isPending}
            >
              {markComplete.isPending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <CheckCheck className="w-5 h-5" />
              )}
              Marquer comme terminé
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
